import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PasswordInput from '../components/PasswordInput';

const accesoRestringidoStyles = {
  container: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' },
  card: { backgroundColor: 'white', padding: '2rem 3rem', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', textAlign: 'center', maxWidth: '500px' },
  icon: { fontSize: '4rem', display: 'block', marginBottom: '1rem' },
  message: { color: '#6b7280', marginBottom: '1.5rem', lineHeight: '1.6' },
  suggestion: { fontSize: '0.9rem', color: '#9ca3af', marginBottom: '2rem' },
  button: { backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', fontSize: '1rem', cursor: 'pointer' },
};

const Usuarios = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [accesoDenegado, setAccesoDenegado] = useState(false);
  const [mensajeError, setMensajeError] = useState('');
  const [usuarios, setUsuarios] = useState([]);
  const [usuariosFiltrados, setUsuariosFiltrados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [limiteUsuarios, setLimiteUsuarios] = useState(null);
  const [infoPlan, setInfoPlan] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nombre: '',
    apellido: '',
    rol: 'usuario',
    puede_cancelar_citas: false,
    puede_ver_clientes: false,
    puede_modificar_clientes: false,
    puede_eliminar_clientes: false,
    puede_configurar: false,
    puede_ver_reportes: false,
    puede_ver_historial: false,
    puede_modificar_servicios: false,
    puede_eliminar_servicios: false,
    puede_modificar_profesionales: false,
    puede_eliminar_profesionales: false,
    puede_enviar_difusion: false,
    activo: true
  });

  // Estilos comunes para inputs
  const inputStyle = {
    border: '2px solid rgba(59, 130, 246, 0.3)',
    borderRadius: '8px',
    padding: '0.75rem',
    fontSize: '1rem',
    backgroundColor: '#fafafa',
    width: '100%',
    transition: 'all 0.2s'
  };

  useEffect(() => {
    if (!authLoading) {
      if (user?.tenant?.acceso_bloqueado === true) {
        setAccesoDenegado(true);
        setMensajeError('Tu suscripción está suspendida. Contacta al administrador.');
        setLoading(false);
        return;
      }
      if (user?.rol !== 'admin') {
        setAccesoDenegado(true);
        setMensajeError('Solo los administradores pueden gestionar usuarios.');
        setLoading(false);
        return;
      }
      fetchUsuarios();
      fetchInfoPlan();
    }
  }, [authLoading]);

  useEffect(() => {
    // Filtrar usuarios cuando cambia el término de búsqueda o la lista
    if (usuarios.length > 0) {
      const filtrados = usuarios.filter(u =>
        u.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.apellido.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setUsuariosFiltrados(filtrados);
    } else {
      setUsuariosFiltrados([]);
    }
  }, [searchTerm, usuarios]);

  const fetchUsuarios = async () => {
    try {
      const { data } = await api.get('/usuarios');
      setUsuarios(data);
      setUsuariosFiltrados(data);
    } catch (error) {
      if (error.response?.status === 403) {
        setAccesoDenegado(true);
        setMensajeError(error.response.data.message || 'No tienes acceso a esta sección.');
      } else {
        toast.error('Error al cargar usuarios');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchInfoPlan = async () => {
    try {
      const { data } = await api.get('/tenant/plan-info');
      
      // Extraer max_usuarios de donde venga
      let maxUsuarios = Number(data.plan?.max_profesionales || data.max_profesionales || 0);
      
      if (data.plan && typeof data.plan.max_usuarios === 'number') {
        maxUsuarios = data.plan.max_usuarios;
        // console.log('📊 Usando data.plan.max_usuarios:', maxUsuarios);
      } else if (typeof data.max_usuarios === 'number') {
        maxUsuarios = data.max_usuarios;
        // console.log('📊 Usando data.max_usuarios:', maxUsuarios);
      } else if (data.plan && data.plan.max_usuarios === 0) {
        maxUsuarios = 0;
        // console.log('📊 Es 0 explícitamente:', maxUsuarios);
      }
      
      setLimiteUsuarios(maxUsuarios);
      setInfoPlan(data);
      
    } catch {
      setLimiteUsuarios(1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar límite de usuarios solo para creación (no para edición)
    if (!editingId) {
      if (limiteUsuarios === 0) {
        toast.error(`❌ Tu plan no permite usuarios. No puedes crear más usuarios.`);
        return;
      }
      
      if (limiteUsuarios > 0 && usuarios.length >= limiteUsuarios) {
        toast.error(`❌ Has alcanzado el límite de ${limiteUsuarios} usuarios de tu plan.`);
        return;
      }
    }

    try {
      if (editingId) {
        await api.put(`/usuarios/${editingId}`, formData);
        toast.success('Usuario actualizado');
      } else {
        await api.post('/usuarios', formData);
        toast.success('Usuario creado');
      }
      fetchUsuarios();
      fetchInfoPlan(); // Actualizar info del plan después de crear
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar usuario');
    }
  };

  const handleEdit = (usuario) => {
    setFormData({
      email: usuario.email,
      password: '', // No mostramos la contraseña
      nombre: usuario.nombre,
      apellido: usuario.apellido,
      rol: usuario.rol,
      puede_cancelar_citas:          usuario.puede_cancelar_citas,
      puede_ver_clientes:            usuario.puede_ver_clientes,
      puede_modificar_clientes:      usuario.puede_modificar_clientes,
      puede_eliminar_clientes:       usuario.puede_eliminar_clientes,
      puede_configurar:              usuario.puede_configurar,
      puede_ver_reportes:            usuario.puede_ver_reportes,
      puede_ver_historial:           usuario.puede_ver_historial,
      puede_modificar_servicios:     usuario.puede_modificar_servicios,
      puede_eliminar_servicios:      usuario.puede_eliminar_servicios,
      puede_modificar_profesionales: usuario.puede_modificar_profesionales,
      puede_eliminar_profesionales:  usuario.puede_eliminar_profesionales,
      puede_enviar_difusion:         usuario.puede_enviar_difusion,
      activo: usuario.activo
    });
    setEditingId(usuario.id);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setConfirmAction({ onConfirm: async () => {
      try {
        await api.delete(`/usuarios/${id}`);
        toast.success('Usuario eliminado');
        fetchUsuarios();
        fetchInfoPlan();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Error al eliminar');
      }
    }});
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData({
      email: '',
      password: '',
      nombre: '',
      apellido: '',
      rol: 'usuario',
      puede_cancelar_citas: false,
      puede_ver_clientes: false,
      puede_modificar_clientes: false,
      puede_eliminar_clientes: false,
      puede_configurar: false,
      puede_ver_reportes: false,
      puede_ver_historial: false,
      puede_modificar_servicios: false,
      puede_eliminar_servicios: false,
      puede_modificar_profesionales: false,
      puede_eliminar_profesionales: false,
      puede_enviar_difusion: false,
      activo: true
    });
  };

  if (accesoDenegado) {
    return (
      <Layout>
        <div style={accesoRestringidoStyles.container}>
          <div style={accesoRestringidoStyles.card}>
            <span style={accesoRestringidoStyles.icon}>🔒</span>
            <h2>Acceso Restringido</h2>
            <p style={accesoRestringidoStyles.message}>{mensajeError}</p>
            <p style={accesoRestringidoStyles.suggestion}>
              Si crees que esto es un error, contacta al administrador del sistema para revisar los detalles de tu plan.
            </p>
            <button
              style={accesoRestringidoStyles.button}
              onClick={() => navigate('/')}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
            >
              Volver al Dashboard
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="text-center" style={{ padding: '3rem' }}>
          <div className="loading"></div>
          <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>
            Cargando usuarios...
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-header">
        <div>
          <h2>👥 Usuarios del Sistema</h2>
          {limiteUsuarios !== null && (
            <div style={{ 
              marginTop: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              flexWrap: 'wrap'
            }}>
              <div style={{ 
                fontSize: '0.875rem',
                color: limiteUsuarios === 0 ? '#dc2626' : 'var(--text-secondary)',
                background: limiteUsuarios === 0 ? '#fee' : 'var(--background)',
                padding: '0.25rem 0.75rem',
                borderRadius: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span>📊 Usuarios: {usuarios.length}</span>
                {limiteUsuarios > 0 && <span>/ {limiteUsuarios}</span>}
              </div>
              
              {limiteUsuarios === 0 && (
                <span style={{ 
                  fontSize: '0.875rem',
                  color: '#dc2626',
                  background: '#fee',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '20px'
                }}>
                  ⚠️ Plan sin usuarios
                </span>
              )}
              
              {limiteUsuarios > 0 && usuarios.length >= limiteUsuarios && (
                <span style={{ 
                  fontSize: '0.875rem',
                  color: '#dc2626',
                  background: '#fee',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '20px'
                }}>
                  ⚠️ Límite alcanzado
                </span>
              )}
            </div>
          )}
        </div>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowModal(true)}
          disabled={limiteUsuarios === 0 || (limiteUsuarios > 0 && usuarios.length >= limiteUsuarios)}
          title={
            limiteUsuarios === 0 
              ? '❌ Tu plan no permite usuarios' 
              : limiteUsuarios > 0 && usuarios.length >= limiteUsuarios 
                ? `❌ Límite alcanzado (${usuarios.length}/${limiteUsuarios})` 
                : ''
          }
        >
          + Nuevo Usuario
        </button>
      </div>

      {/* Buscador */}
      <div className="card mb-3">
        <input
          type="text"
          className="form-input"
          placeholder="🔍 Buscar usuario por nombre, apellido o email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={inputStyle}
          onFocus={(e) => {
            e.target.style.borderColor = 'rgba(59, 130, 246, 0.6)';
            e.target.style.backgroundColor = '#ffffff';
            e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'rgba(59, 130, 246, 0.3)';
            e.target.style.backgroundColor = '#fafafa';
            e.target.style.boxShadow = 'none';
          }}
        />
        {searchTerm && (
          <div style={{ 
            marginTop: '0.5rem', 
            fontSize: '0.875rem', 
            color: 'var(--text-secondary)',
            textAlign: 'right'
          }}>
            {usuariosFiltrados.length} resultado(s) encontrado(s)
          </div>
        )}
      </div>

      {limiteUsuarios > 0 && usuarios.length >= limiteUsuarios &&(
        <div className="alert alert-warning" style={{ marginBottom: '1rem' }}>
          ⚠️ Has alcanzado el límite de {limiteUsuarios} usuarios de tu plan. 
          Actualiza tu plan para agregar más usuarios.
        </div>
      )}

      {limiteUsuarios === 0 && (
        <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
          ⚠️ Tu plan no permite usuarios. Actualiza tu plan para poder crear usuarios.
        </div>
      )}

      {usuariosFiltrados.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <span style={{ fontSize: '3rem' }}>👥</span>
          <h3 style={{ marginTop: '1rem' }}>
            {searchTerm ? 'No hay resultados para tu búsqueda' : 'No hay usuarios creados'}
          </h3>
          <p style={{ color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
            {searchTerm 
              ? 'Prueba con otros términos de búsqueda' 
              : 'Crea tu primer usuario haciendo click en el botón "+ Nuevo Usuario"'}
          </p>
        </div>
      ) : (
        <div className="card">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Rol</th>
                  <th>Permisos</th>
                  <th>Estado</th>
                  <th>Último Acceso</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {usuariosFiltrados.map(usuario => (
                  <tr key={usuario.id}>
                    <td>
                      <strong>{usuario.nombre} {usuario.apellido}</strong>
                    </td>
                    <td>{usuario.email}</td>
                    <td>
                      <span className={`badge ${usuario.rol === 'admin' ? 'badge-success' : 'badge-secondary'}`}>
                        {usuario.rol}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                        {usuario.puede_ver_clientes            && <span>👥 Ver clientes · </span>}
                        {usuario.puede_modificar_clientes      && <span>✏️ Mod. clientes · </span>}
                        {usuario.puede_eliminar_clientes       && <span>🗑️ Elim. clientes · </span>}
                        {usuario.puede_cancelar_citas          && <span>❌ Cancelar citas · </span>}
                        {usuario.puede_ver_reportes            && <span>📈 Reportes · </span>}
                        {usuario.puede_ver_historial           && <span>📋 Historial · </span>}
                        {usuario.puede_modificar_servicios     && <span>✏️ Mod. servicios · </span>}
                        {usuario.puede_eliminar_servicios      && <span>🗑️ Elim. servicios · </span>}
                        {usuario.puede_modificar_profesionales && <span>✏️ Mod. profesionales · </span>}
                        {usuario.puede_eliminar_profesionales  && <span>🗑️ Elim. profesionales · </span>}
                        {usuario.puede_enviar_difusion         && <span>📢 Difusión · </span>}
                        {usuario.puede_configurar              && <span>⚙️ Configuración</span>}
                      </div>
                    </td>
                    <td>
                      {usuario.activo ? (
                        <span className="badge badge-success">Activo</span>
                      ) : (
                        <span className="badge badge-danger">Inactivo</span>
                      )}
                    </td>
                    <td style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      {usuario.ultimo_acceso ? new Date(usuario.ultimo_acceso).toLocaleDateString() : 'Nunca'}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button 
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEdit(usuario)}
                        >
                          ✏️
                        </button>
                        {usuario.rol !== 'admin' && (
                          <button 
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(usuario.id)}
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.5rem 1.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  width: '42px', height: '42px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.3rem',
                }}>
                  👤
                </div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                    {editingId ? 'Editar Usuario' : 'Nuevo Usuario'}
                  </h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                    {editingId ? 'Modifica la información del usuario' : 'Completa los datos para agregar un nuevo usuario'}
                  </p>
                </div>
              </div>
              <button
                onClick={closeModal}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '32px', height: '32px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body" style={{ padding: '1.5rem 1.75rem', maxHeight: '70vh', overflowY: 'auto' }}>

                {/* Datos personales */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>👤 Datos personales</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    {[
                      { key: 'nombre',   label: 'Nombre',   required: true, placeholder: 'Ej. Juan' },
                      { key: 'apellido', label: 'Apellido', required: true, placeholder: 'Ej. Pérez' },
                    ].map(({ key, label, required, placeholder }) => (
                      <div key={key} className="form-group">
                        <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.875rem' }}>
                          {label} <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <input type="text" className="form-input" value={formData[key]}
                          onChange={(e) => setFormData({...formData, [key]: e.target.value})}
                          required placeholder={placeholder} style={inputStyle}
                          onFocus={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                          onBlur={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; e.target.style.boxShadow = 'none'; }}
                        />
                      </div>
                    ))}
                    <div className="form-group">
                      <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.875rem' }}>
                        Email <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input type="email" className="form-input" value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required placeholder="correo@ejemplo.com" style={inputStyle}
                        onFocus={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; e.target.style.boxShadow = 'none'; }}
                      />
                    </div>
                    <div className="form-group">
                      <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>
                        {editingId ? 'Nueva contraseña (vacío = mantener)' : <>{`Contraseña`} <span style={{ color: '#ef4444' }}>*</span></>}
                      </label>
                      <PasswordInput className="form-input" value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        required={!editingId} placeholder="Mínimo 6 caracteres" style={inputStyle}
                        onFocus={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; e.target.style.boxShadow = 'none'; }}
                      />
                    </div>
                  </div>
                </div>

                {/* Rol y estado */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#f0fdf4', color: '#16a34a', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>⚙️ Rol y estado</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', alignItems: 'center' }}>
                    <div className="form-group">
                      <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Rol</label>
                      <select className="form-input" value={formData.rol}
                        onChange={(e) => setFormData({...formData, rol: e.target.value})}
                        style={{ ...inputStyle, cursor: 'pointer' }}
                        onFocus={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; }}
                      >
                        <option value="usuario">Usuario</option>
                        <option value="admin">Administrador</option>
                      </select>
                    </div>
                    <div style={{ paddingTop: '1.5rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                        <input type="checkbox" checked={formData.activo}
                          onChange={(e) => setFormData({...formData, activo: e.target.checked})}
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                        Usuario activo
                      </label>
                    </div>
                  </div>
                </div>

                {/* Permisos */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#fdf4ff', color: '#7c3aed', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>🔐 Permisos</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                    {[
                      { key: 'puede_cancelar_citas',          label: 'Cancelar citas' },
                      { key: 'puede_ver_clientes',            label: 'Ver clientes' },
                      { key: 'puede_modificar_clientes',      label: 'Modificar clientes' },
                      { key: 'puede_eliminar_clientes',       label: 'Eliminar clientes' },
                      { key: 'puede_ver_reportes',            label: 'Ver reportes' },
                      { key: 'puede_ver_historial',           label: 'Ver historial' },
                      { key: 'puede_modificar_servicios',     label: 'Modificar servicios' },
                      { key: 'puede_eliminar_servicios',      label: 'Eliminar servicios' },
                      { key: 'puede_modificar_profesionales', label: 'Modificar profesionales' },
                      { key: 'puede_eliminar_profesionales',  label: 'Eliminar profesionales' },
                      { key: 'puede_enviar_difusion',         label: 'Enviar difusiones' },
                      { key: 'puede_configurar',              label: 'Acceder configuración' },
                    ].map(({ key, label }) => (
                      <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', color: '#374151' }}>
                        <input type="checkbox" checked={!!formData[key]}
                          onChange={(e) => setFormData({...formData, [key]: e.target.checked})}
                          style={{ width: '15px', height: '15px', cursor: 'pointer' }} />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid #e5e7eb', padding: '1rem 1.75rem' }}>
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
                <button type="submit" className="btn btn-primary" style={{ minWidth: '120px' }}>
                  {editingId ? 'Guardar cambios' : '+ Crear usuario'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {confirmAction && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.25rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                <h3 style={{ margin: 0, color: 'white', fontSize: '1rem', fontWeight: '700' }}>
                  Confirmar acción
                </h3>
              </div>
              <button
                onClick={() => setConfirmAction(null)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '28px', height: '28px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#374151' }}>¿Confirmas esta acción?</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setConfirmAction(null)}>No</button>
              <button className="btn btn-primary" onClick={() => { confirmAction.onConfirm(); setConfirmAction(null); }}>Sí</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Usuarios;
